import { TranscriptionConfig } from './types'
import { Checkbox, NumberSlider, ButtonGroup, Select } from './FormControls'

interface BasicTabProps {
  config: TranscriptionConfig
  onChange: (config: TranscriptionConfig) => void
}

export function BasicTab({ config, onChange }: BasicTabProps) {
  const update = (key: keyof TranscriptionConfig, value: unknown) => {
    onChange({ ...config, [key]: value })
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <ButtonGroup
          label="Resolution"
          value={config.resolution}
          options={[
            { value: 16, label: '16' },
            { value: 24, label: '24' },
            { value: 32, label: '32' },
            { value: 48, label: '48' },
            { value: 64, label: '64' },
          ]}
          onChange={(v) => update('resolution', v)}
          tooltip="Temporal resolution for note detection. Higher = more precise timing."
        />

        <NumberSlider
          label="BPM"
          value={config.bpm}
          onChange={(v) => update('bpm', v)}
          min={60}
          max={200}
          step={1}
          allowNull
          nullLabel="Auto-detect"
          tooltip="Tempo in beats per minute. Leave as Auto to detect automatically."
        />

        <Checkbox
          label="Smart Sound Selection"
          value={config.smartSounds}
          onChange={(v) => update('smartSounds', v)}
          tooltip="Use AI to select optimal sounds from sample library"
        />

        <Checkbox
          label="Unified Selector (v10)"
          value={config.unifiedSelector}
          onChange={(v) => update('unifiedSelector', v)}
          tooltip="Use the v10 unified sound selector for better sample matching"
        />

        <Checkbox
          label="Humanize"
          value={config.humanize}
          onChange={(v) => update('humanize', v)}
          tooltip="Apply expression and timing humanization"
        />

        <Checkbox
          label="Detect Chords"
          value={config.detectChords}
          onChange={(v) => update('detectChords', v)}
          tooltip="Detect chord progressions and generate harmony track"
        />

        <Checkbox
          label="Swing"
          value={config.swing}
          onChange={(v) => update('swing', v)}
          tooltip="Apply swing timing to drums and rhythm"
        />

        <Checkbox
          label="Electronic Mode"
          value={config.electronic}
          onChange={(v) => update('electronic', v)}
          tooltip="Optimize for electronic/programmed music (tight quantization)"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Select
          label="Quantize Grid"
          value={config.quantize}
          options={[
            { value: 0, label: 'Off (No Quantization)' },
            { value: 8, label: '8th Notes' },
            { value: 16, label: '16th Notes' },
            { value: 32, label: '32nd Notes' },
            { value: 64, label: '64th Notes' },
          ]}
          onChange={(v) => update('quantize', Number(v))}
          tooltip="Snap notes to grid. Off = preserve original timing."
        />

        <div className="space-y-3">
          <Checkbox
            label="Duration (@N)"
            value={config.duration}
            onChange={(v) => update('duration', v)}
            tooltip="Include note durations in output"
          />

          <Checkbox
            label="Merge Notes"
            value={config.merge}
            onChange={(v) => update('merge', v)}
            tooltip="Merge consecutive identical notes"
          />
        </div>
      </div>
    </div>
  )
}
